/**
 * Browser exception capture that does not depend on PostHog's external
 * exception-autocapture script. Duplicate $exception events from the SDK
 * and these listeners are collapsed in before_send.
 */

const DEDUPE_WINDOW_MS = 1000;

const recentExceptions = new Map<string, number>();

export interface ExceptionClient {
  captureException: (
    error: unknown,
    properties?: Record<string, unknown>,
  ) => unknown;
}

export interface ExceptionEvent {
  event?: string;
  properties?: Record<string, unknown>;
}

let unhandledCaptureInstalled = false;

export function resetExceptionDedupe(): void {
  recentExceptions.clear();
}

export function resetUnhandledExceptionCaptureForTests(): void {
  unhandledCaptureInstalled = false;
}

export function exceptionFingerprint(
  properties: Record<string, unknown> | undefined,
): string {
  const list = properties?.$exception_list;
  if (Array.isArray(list) && list.length > 0 && list[0] && typeof list[0] === 'object') {
    const item = list[0] as { type?: unknown; value?: unknown };
    const type = typeof item.type === 'string' ? item.type : '';
    const value = typeof item.value === 'string' ? item.value.slice(0, 200) : '';
    return `${type}|${value}`;
  }
  return 'exception';
}

export function dedupeExceptionEvent<T extends ExceptionEvent>(
  event: T | null | undefined,
  now: number = Date.now(),
): T | null {
  if (!event) {
    return null;
  }
  if (event.event !== '$exception') {
    return event;
  }
  const key = exceptionFingerprint(event.properties);
  const previous = recentExceptions.get(key);
  if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) {
    return null;
  }
  recentExceptions.set(key, now);
  if (recentExceptions.size > 100) {
    for (const [storedKey, seenAt] of recentExceptions) {
      if (now - seenAt >= DEDUPE_WINDOW_MS) {
        recentExceptions.delete(storedKey);
      }
    }
  }
  return event;
}

export function installUnhandledExceptionCapture(client: ExceptionClient): void {
  if (unhandledCaptureInstalled || typeof window === 'undefined') {
    return;
  }
  unhandledCaptureInstalled = true;

  window.addEventListener('error', (event) => {
    const error =
      event.error instanceof Error
        ? event.error
        : new Error(event.message || 'Unhandled error');
    try {
      client.captureException(error, { mechanism: 'window.onerror' });
    } catch {
      // Exception reporting must not break the page.
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error('Unhandled promise rejection');
    try {
      client.captureException(error, { mechanism: 'unhandledrejection' });
    } catch {
      // Exception reporting must not break the page.
    }
  });
}

export function reportReactBoundaryException(
  client: ExceptionClient | null | undefined,
  error: unknown,
  incidentId?: string,
): void {
  if (!client || typeof client.captureException !== 'function') {
    return;
  }
  const errorObject =
    error instanceof Error ? error : new Error('React render error');
  const properties: Record<string, unknown> = {
    source: 'react_error_boundary',
  };
  if (incidentId) {
    properties.incident_id = incidentId;
  }
  try {
    client.captureException(errorObject, properties);
  } catch {
    // The fallback UI must still render.
  }
}
